import React, { useMemo, memo, useState, useCallback } from 'react';
import { DraggableList, DraggableListItem } from '@campfire/draggable-list';
import { Box } from '@material-ui/core';
import { CategoriesListItem } from './CategoriesListItem';
import { SupportCategory } from '../__generated__/SupportCategory';
import { useSupportContext } from '../SupportContext';

interface CategoriesListProps {
  categories: SupportCategory[];
  reorderMode?: boolean;
}

const CategoriesList = memo((props: CategoriesListProps) => {
  const { categories, reorderMode } = props;
  const { runReorderCategories } = useSupportContext();

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.title < b.title ? -1 : 1)),
    [categories]
  );

  const [orderedCategories, setOrderedCategories] = useState(sortedCategories);

  React.useEffect(() => {
    setOrderedCategories(sortedCategories);
  }, [sortedCategories]);

  const handleReorder = useCallback(
    (startIdx: number, endIdx: number, reorderFn: (list: any[], s: number, e: number) => any[]) => {
      const reordered = reorderFn(orderedCategories, startIdx, endIdx) as SupportCategory[];
      setOrderedCategories(reordered);
      runReorderCategories(
        reordered.map((cat, idx) => ({
          supportCategoryId: cat.supportCategoryId,
          sortOrder: idx,
        }))
      );
    },
    [orderedCategories, runReorderCategories]
  );

  if (reorderMode) {
    return (
      <DraggableList onReorder={handleReorder}>
        {orderedCategories.map((category) => (
          <DraggableListItem
            key={category.supportCategoryId}
            id={category.supportCategoryId}
            index={0}
            style={{ padding: 0, background: 'transparent' }}
          >
            <Box width='100%'>
              <CategoriesListItem category={category} reorderMode />
            </Box>
          </DraggableListItem>
        ))}
      </DraggableList>
    );
  }

  return (
    <Box>
      {orderedCategories.map((category) => (
        <CategoriesListItem key={category.supportCategoryId} category={category} />
      ))}
    </Box>
  );
});

export { CategoriesList };
